import React, { Component } from 'react'
import { connect } from 'react-redux'

import { fetchOpex, updateDecisions, fetchOpexBackground } from '../../../redux/opex'
import { fetchSummary } from '../../../redux/summary'

import CenteredSpinner from '../../Atoms/Spinner/CenteredSpinner'
import InlineError from '../../Atoms/InlineError'
import Opex from './component'
import { getImpacts, fteCostPerDivision } from './calculations'

const anyVersionsChanged = (newOpex, oldOpex, deviceId) => {
  let hasChange = false
  if (newOpex.version !== oldOpex.version && newOpex.version > oldOpex.version && newOpex.updatedBy && newOpex.updatedBy !== deviceId) {
    hasChange = true
  }
  return hasChange
}

const checkChange = (fieldName, decisions, lastSynced, changes, apiFieldName) => {
  const newVal = parseInt(decisions[fieldName])
  if (newVal !== lastSynced[fieldName]) {
    changes.push({ [apiFieldName || fieldName]: newVal })
    lastSynced[fieldName] = newVal
  }
}

class OpexContainer extends Component {
  constructor (props) {
    super(props)
    this.state = {
      selectedDivision: null,
      decisions: null,
      readyForDisplay: false,
      forceRerender: null
    }
    this.lastSynced = {}
  }

  componentDidMount () {
    this.props.fetchOpex(this.props.teamNumber, this.props.selectedRound)
    this.pollChanges()
    if (!this.props.summaries[this.props.selectedRound]) {
      this.props.fetchSummary(this.props.teamNumber, this.props.selectedRound)
    }
  }

  componentWillUnmount () {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
      this.checkAndUpdate()
      this.lastSynced = {}
    }
  }

  shouldComponentUpdate (nextProps) {
    if (!nextProps.isCurrentTab) {
      return false
    }
    return true
  }

  pollChanges = () => {
    this.interval = setInterval(() => {
      if (this.props.isCurrentTab) {
        this.checkAndUpdate()
        this.props.fetchOpexBackground(this.props.teamNumber, this.props.selectedRound)
      }
    }, 5000)
  }

  checkAndUpdate = () => {
    const changes = []
    const decisions = { ...this.state.decisions }

    if (!Object.keys(this.lastSynced).length) {
      return
    }

    const { divisions } = this.props.opex

    for (const division of divisions) {
      checkChange(division.type, decisions, this.lastSynced, changes, `division${division.id}Change`)
    }

    checkChange('employeeEngagementClosingScore', decisions, this.lastSynced, changes, 'employeeEngagementClosingScore')
    checkChange('npsClosingScore', decisions, this.lastSynced, changes, 'npsClosingScore')
    checkChange('complaintsClosing', decisions, this.lastSynced, changes, 'complaintsClosing')
    checkChange('riskIncidentsClosing', decisions, this.lastSynced, changes, 'riskIncidentsClosing')
    checkChange('regulatorActionsClosing', decisions, this.lastSynced, changes, 'regulatorActionsClosing')
    checkChange('communityTrustClosing', decisions, this.lastSynced, changes, 'communityTrustClosing')
    checkChange('fteChangeDivision1', decisions, this.lastSynced, changes, 'fteChangeDivision1')
    checkChange('fteChangeDivision2', decisions, this.lastSynced, changes, 'fteChangeDivision2')
    checkChange('fteChangeDivision3', decisions, this.lastSynced, changes, 'fteChangeDivision3')
    checkChange('fteChangeDivision4', decisions, this.lastSynced, changes, 'fteChangeDivision4')
    checkChange('salaryChange', decisions, this.lastSynced, changes, 'salaryChange')

    if (changes.length) {
      // console.log(changes)
      this.props.updateDecisions({ team: this.props.teamNumber, round: this.props.selectedRound, decisions: changes, deviceId: this.props.deviceId })
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.props.selectedRound !== prevProps.selectedRound || this.props.roundNumber !== prevProps.roundNumber || this.props.teamNumber !== prevProps.teamNumber) {
      this.lastSynced = {}
      if (!this.props.summaries[this.props.selectedRound]) {
        this.props.fetchSummary(this.props.teamNumber, this.props.selectedRound)
      }
      return this.props.fetchOpex(this.props.teamNumber, this.props.selectedRound)
    }
    const shouldUpdateDecisions =
      (this.props.opex && !prevProps.opex) ||
      (this.props.opex && !prevState.readyForDisplay && !this.state.readyForDisplay) ||
      (this.props.opex && prevProps.opex && anyVersionsChanged(this.props.opex, prevProps.opex, prevProps.deviceId))

    if (shouldUpdateDecisions) {
      this.lastSynced = {
        employeeEngagementClosingScore: this.props.opex.employeeEngagementClosingScore,
        npsClosingScore: this.props.opex.npsClosingScore,
        complaintsClosing: this.props.opex.complaintsClosing,
        riskIncidentsClosing: this.props.opex.riskIncidentsClosing,
        regulatorActionsClosing: this.props.opex.regulatorActionsClosing,
        communityTrustClosing: this.props.opex.communityTrustClosing,
        fteChangeDivision1: this.props.opex.fteChangeDivision1,
        fteChangeDivision2: this.props.opex.fteChangeDivision2,
        fteChangeDivision3: this.props.opex.fteChangeDivision3,
        fteChangeDivision4: this.props.opex.fteChangeDivision4,
        salaryChange: this.props.opex.salaryChange
      }
      let decisions = this.props.opex.divisions.reduce((result, division) => {
        const val = division.value_change // this.props.opex[`division${division.id}Change`]
        this.lastSynced[division.type] = val
        result[division.type] = val
        return result
      }, {})
      const derivedState = this.getDerivedState(this.props.opex, decisions)
      decisions = { ...decisions, ...derivedState }

      this.setState({
        readyForDisplay: true,
        decisions: decisions,
        selectedDivision: prevState.selectedDivision || this.props.opex.divisions[0].type
      })
    }
  }

  getDerivedState = (opex, decisions) => {
    const { employeeEngagement, nps, complaints, riskIncidents, regulatorActions, communityTrust } = getImpacts(opex, decisions)
    const peopleAndCostImpactPerDivision = fteCostPerDivision(opex, decisions)
    return {
      employeeEngagementClosingScore: employeeEngagement.value,
      npsClosingScore: nps.value * 100,
      complaintsClosing: complaints.value,
      riskIncidentsClosing: riskIncidents.value,
      regulatorActionsClosing: regulatorActions.value,
      communityTrustClosing: communityTrust.value,
      fteChangeDivision1: peopleAndCostImpactPerDivision[0].fteChange.toFixed(0),
      fteChangeDivision2: peopleAndCostImpactPerDivision[1].fteChange.toFixed(0),
      fteChangeDivision3: peopleAndCostImpactPerDivision[2].fteChange.toFixed(0),
      fteChangeDivision4: peopleAndCostImpactPerDivision[3].fteChange.toFixed(0),
      salaryChange: peopleAndCostImpactPerDivision[4].changeInCost.toFixed(0)
    }
  }

  handleChangeValue = (changes) => {
    const actualChanges = { ...changes }
    const forceRerender = actualChanges.forceRerender
    delete actualChanges.forceRerender

    const stateToUpdate = {
      decisions: {
        ...this.state.decisions,
        ...changes
      }
    }

    const derivedState = this.getDerivedState(this.props.opex, stateToUpdate.decisions)
    stateToUpdate.decisions = { ...stateToUpdate.decisions, ...derivedState }

    if (forceRerender) {
      stateToUpdate.forceRerender = forceRerender
    }
    this.setState(stateToUpdate)
  }

  handleDivisionSelect = (selectedDivision) => {
    this.setState({ selectedDivision })
  }

  render () {
    if (this.props.isLoading || !this.state.readyForDisplay || !this.props.summaries[this.props.selectedRound] || !this.props.summaries[this.props.selectedRound].length) {
      return <CenteredSpinner />
    }
    if (this.props.error) {
      return <InlineError message={this.props.error.message} />
    }
    if (!this.props.opex) {
      return <InlineError message={`No opex data has been uploaded for round ${this.props.selectedRound}`} />
    }

    return (
      <Opex
        opex={this.props.opex}
        selectedDivision={this.state.selectedDivision}
        summaries={this.props.summaries[this.props.selectedRound]}
        onDivisionSelect={this.handleDivisionSelect}
        selectedRound={this.props.selectedRound}
        decisions={this.state.decisions}
        onChangeValue={this.handleChangeValue}
        currentPage={this.props.currentPage}
        updatePage={this.props.updatePage}
        enableChanges={this.props.enableChanges}
        forceRerender={this.state.forceRerender}
      />
    )
  }
}

const mapStateToProps = (state) => ({
  isCurrentTab: state.ui.currentTab === 'Opex',
  enableChanges: state.game.selectedRound === state.game.currentRound,
  teamNumber: state.team.id,
  selectedRound: state.game.selectedRound,
  deviceId: state.device.deviceId,
  roundNumber: state.game.currentRound,
  isLoading: state.opex.isLoading,
  isLoaded: state.opex.isLoaded,
  error: state.opex.error,
  opex: state.opex.opex,
  summaries: state.summary.summaries
})

export default connect(mapStateToProps, {
  fetchOpex,
  fetchOpexBackground,
  updateDecisions,
  fetchSummary
})(OpexContainer)
